/**
 * "Use a code from another device": the save under that code replaces this device's, which is
 * kept as a backup (with the code it was using, so that cloud save can still be reached). Nothing
 * local is touched until the server has answered with a save - a wrong code changes nothing.
 */

import { keepBackup } from './backup';
import type { LocalStore } from './localStore';
import { MSG, refusedMessage } from './messages';
import { isValidCode, normaliseCode } from './saveCode';
import { fingerprint, freshState } from './state';
import type { Transport } from './transport';
import type { CloudKey, CloudState, KeyValueStore } from './types';
import { CLOUD_KEY, CLOUD_KEYS } from './types';

export interface AdoptInput {
  storage: KeyValueStore;
  transport: Transport;
  state: CloudState;
  text: string;
  now(): number;
  local: LocalStore;
}

/** `state` is the new bookkeeping when the save was replaced; otherwise null and `message` says why not. */
export interface AdoptOutcome {
  state: CloudState | null;
  message: string;
}

export async function adoptCode(input: AdoptInput): Promise<AdoptOutcome> {
  const { storage, state } = input;
  const { read, write } = input.local;
  const refuse = (message: string): AdoptOutcome => ({ state: null, message });
  const code = normaliseCode(input.text);
  // Checked here so a slip of the finger never becomes a request, let alone a lookup.
  if (!isValidCode(code)) return refuse(MSG.badCode);
  if (code === state.code) return refuse(MSG.sameCode);

  const got = await input.transport.get(code);
  if (got.kind === 'none') return refuse(MSG.noSave);
  if (got.kind === 'refused') return refuse(refusedMessage(got.status));
  if (got.kind !== 'ok') return refuse(MSG.unreachable);

  const mine: Partial<Record<CloudKey, string>> = {};
  for (const key of CLOUD_KEYS) {
    const value = read(key);
    if (value !== null && value !== '') mine[key] = value;
  }
  // The backup comes first and is tracked in the live state, so it is pruned one day even if
  // the rest fails. No backup, no replacement: a save is never destroyed to make room for another.
  const kept = keepBackup(storage, state, {
    at: input.now(), why: 'use-code', side: 'local', previousCode: state.code, keys: mine,
  });
  if (kept === null && Object.keys(mine).length > 0) return refuse(MSG.noRoom);
  const profile = got.doc.keys[CLOUD_KEY.profile];
  // Then the profile: if even that does not fit, nothing has been replaced yet.
  if (profile !== undefined && !write(CLOUD_KEY.profile, profile)) return refuse(MSG.noRoom);

  const next = freshState(code);
  next.backups = [...state.backups];
  for (const key of CLOUD_KEYS) {
    const value = got.doc.keys[key];
    if (value !== undefined && (key === CLOUD_KEY.profile || write(key, value))) {
      next.base[key] = fingerprint(value);
      continue;
    }
    // Not part of that save (or no room for it): this device's copy belongs to the old save and must not linger.
    try {
      storage.removeItem(key);
    } catch {
      write(key, '');
    }
  }
  next.revision = got.doc.revision;
  next.syncedAt = input.now();
  return { state: next, message: MSG.replaced };
}
